import { CloseCircle, DocumentDownload } from 'iconsax-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { AppRoutes } from '@/constants/appRoutes'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { usePdfDownload } from '@/hooks/usePdfDownload'
import { cn } from '@/lib/utils'
import {
  ESocialCheckStatus,
  EWebhookEvent,
  EReportType as EEnterpriseReportType,
} from '@/store/api/Enterprise/Enterprise.types'
import {
  useMakeBackgroundCheckOpenedMutation,
  useReDownloadPdfMutation,
} from '@/store/api/Person/Person.query'
import type {
  ReportCheckData,
  SubjectInputs,
} from '@/store/api/Workspace/Workspace.types'
import { formatDate } from '@/utils/date'
import { createSafeWindowOpener } from '@/utils/openUrlSafely'
import { weCheckPalette } from '@/utils/palette/weCheckPalette'

import { CancelReportDialog } from './CancelReportDialog'
import {
  ReportStatusPill,
  resolveSocialCheckPillVariant,
} from './ReportStatusPill'

const AI_REPORT_IN_PROGRESS_STATUSES = new Set<ESocialCheckStatus>([
  ESocialCheckStatus.InQueue,
  ESocialCheckStatus.InProgress,
  ESocialCheckStatus.PendingToScrapProfiles,
  ESocialCheckStatus.ScrapeInProgress,
  ESocialCheckStatus.IaAnalysisInProgress,
])

interface SocialAnalysisItemProps {
  data: ReportCheckData
  showActions?: boolean
}

export const SocialAnalysisItem = ({
  data,
  showActions = true,
}: SocialAnalysisItemProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [reDownloadPDF, { isLoading: isDownloadingPdf }] =
    useReDownloadPdfMutation()
  const [makeBackgroundCheckOpened] = useMakeBackgroundCheckOpenedMutation()
  const { downloadPdf } = usePdfDownload({ socialCheckId: data.id })

  const initials = useMemo(
    () => extractInputInitials(data.inputs),
    [data.inputs],
  )
  const pillVariant = resolveSocialCheckPillVariant(data)

  const isComplete = data.status === ESocialCheckStatus.Complete
  const isPendingConsent =
    data.status === ESocialCheckStatus.NotReleased &&
    data.complianceStatus !== EWebhookEvent.COMPLETED
  const isDownloadable = isComplete && !!data.pdf
  const isAiReportInProgress =
    data.reportType === EEnterpriseReportType.Ai &&
    AI_REPORT_IN_PROGRESS_STATUSES.has(data.status as ESocialCheckStatus)
  const isRowClickable = isComplete || isAiReportInProgress

  const handleDownload = async () => {
    if (data.pdf) {
      await downloadPdf('pdf')
    } else if (data.status === 'PendingDownloadPdf') {
      const opener = createSafeWindowOpener()
      try {
        const res = await reDownloadPDF({ id: data.id }).unwrap()
        if (res.info.url) opener.open(res.info.url)
        else opener.close()
      } catch (e) {
        opener.close()
        catchWithSentry(e)
      }
    }
    try {
      await makeBackgroundCheckOpened({ id: data.id }).unwrap()
    } catch (e) {
      catchWithSentry(e)
    }
  }

  const handleRowClick = () => {
    if (isAiReportInProgress) {
      const params = new URLSearchParams({
        subjectId: data.subjectId,
        reportId: data.id,
      })
      navigate(`${AppRoutes.generatingReport}?${params.toString()}`)
      return
    }
    navigate(AppRoutes.digitalReport, {
      state: { id: data.id, subjectId: data.subjectId },
    })
    makeBackgroundCheckOpened({ id: data.id }).catch(catchWithSentry)
  }

  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleRowClick()
    }
  }

  return (
    <div
      role={isRowClickable ? 'button' : undefined}
      tabIndex={isRowClickable ? 0 : undefined}
      onClick={isRowClickable ? handleRowClick : undefined}
      onKeyDown={isRowClickable ? handleRowKeyDown : undefined}
      className={cn(
        'flex items-center w-full border-b border-neutralColor5 last:border-b-0 gap-2 min-h-[72px]',
        isRowClickable &&
          'cursor-pointer hover:bg-baseColor transition-colors focus-visible:outline-none focus-visible:bg-baseColor',
      )}
    >
      <div className="flex flex-1 min-w-0 items-center gap-2 px-2">
        <Avatar className="w-8 h-8 shrink-0">
          <AvatarImage src={data.profileImgUrl} />
          <AvatarFallback className="bg-baseColor text-neutralColor text-xs">
            {initials}
          </AvatarFallback>
        </Avatar>
        <p className="text-sm text-neutralColor truncate">{data.inputs.name}</p>
      </div>

      <div className="hidden md:block flex-1 min-w-0 px-2">
        <p className="text-sm text-neutralColor truncate">
          {getReportLabel(data.reportType, t)}
        </p>
      </div>

      <div className="hidden lg:block flex-1 min-w-0 px-2">
        <p className="text-sm text-neutralColor">
          {formatDate(data.reportDate, 'MM/dd/yyyy')}
        </p>
      </div>

      <div className="hidden lg:block flex-1 min-w-0 px-2">
        <p className="text-sm text-neutralColor truncate">
          {t('dashboard.reportsScreen.by')} {data.requester.firstname}{' '}
          {data.requester.lastname}
        </p>
      </div>

      <div className="w-[135px] flex justify-end px-2 shrink-0">
        {pillVariant && <ReportStatusPill variant={pillVariant} />}
      </div>

      {showActions && (
        <div className="w-10 flex justify-end mr-2 shrink-0">
          {isDownloadable && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                handleDownload()
              }}
              disabled={isDownloadingPdf}
              aria-label={t('dashboard.reportsScreen.download')}
              className="p-1.5 rounded-md hover:bg-baseColorHighlight transition-colors disabled:opacity-50"
            >
              <DocumentDownload size={20} color={weCheckPalette.neutralColor} />
            </button>
          )}

          {isPendingConsent && (
            <CancelReportDialog id={data.id}>
              <button
                type="button"
                onClick={(e) => e.stopPropagation()}
                aria-label={t('dashboard.reportsScreen.cancelReportGeneration')}
                className="p-1.5 rounded-md hover:bg-baseColorHighlight transition-colors"
              >
                <CloseCircle size={20} color={weCheckPalette.neutralColor} />
              </button>
            </CancelReportDialog>
          )}
        </div>
      )}
    </div>
  )
}

const extractInputInitials = (input: SubjectInputs): string => {
  const fullname = `${input.name ?? ''} ${input.lastName ?? ''}`.trim()
  if (!fullname) return ''
  const parts = fullname.split(/\s+/)
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : ''
  return `${first}${last}`.toUpperCase()
}

const getReportLabel = (
  reportType: EEnterpriseReportType,
  t: (key: string) => string,
): string => {
  switch (reportType) {
    case EEnterpriseReportType.AdverseMedia:
      return t('dashboard.reportsScreen.adverseMedia')
    case EEnterpriseReportType.Ai:
      return t('dashboard.reportsScreen.aIReport')
    case EEnterpriseReportType.AiAnalyst:
      return t('dashboard.reportsScreen.aIAnalystReport')
    case EEnterpriseReportType.Assited:
      return t('dashboard.reportsScreen.weCheckAssisted')
    default:
      return ''
  }
}
